import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Search, ChevronLeft, ChevronRight, X,
  AlertOctagon, BookOpen, Inbox,
} from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import './AdminLayout.css';
import './AdminAds.css';

const STATUS_LABELS_EN = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  active: 'Active',
  hidden_by_user: 'Hidden by user',
  sold: 'Sold',
  rejected: 'Rejected',
  hidden_by_reports: 'Hidden (reports)',
  removed_by_moderation: 'Removed',
  needs_photo_reupload: 'Needs Photo Reupload',
};
const STATUS_LABELS_AR = {
  draft: 'مسودة',
  pending_review: 'قيد المراجعة',
  active: 'نشط',
  hidden_by_user: 'مخفي بواسطة المالك',
  sold: 'مباع',
  rejected: 'مرفوض',
  hidden_by_reports: 'مخفي (بلاغات)',
  removed_by_moderation: 'مزال',
  needs_photo_reupload: 'بحاجة لإعادة رفع الصورة',
};
const STATUS_PILL = {
  active: 'green',
  pending_review: 'yellow',
  rejected: 'red',
  removed_by_moderation: 'red',
  hidden_by_reports: 'red',
  hidden_by_user: 'gray',
  sold: 'blue',
  draft: 'gray',
  // iter278 — neutral cleanup state.
  needs_photo_reupload: 'yellow',
};
const ALL_STATUSES = Object.keys(STATUS_LABELS_EN);
const PAGE_SIZE = 25;

export default function AdminBooks() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const location = useLocation();
  const isArabic = lang === 'ar';
  const labels = isArabic ? STATUS_LABELS_AR : STATUS_LABELS_EN;

  const initialStatusFromUrl = (() => {
    const hash = location.hash || '';
    const qIdx = hash.indexOf('?');
    const params = new URLSearchParams(
      qIdx >= 0 ? hash.slice(qIdx + 1) : (location.search || '').replace(/^\?/, '')
    );
    const s = params.get('status') || '';
    return ALL_STATUSES.includes(s) ? s : '';
  })();

  const [status, setStatus] = useState(initialStatusFromUrl);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const rawToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    const liveToken =
      rawToken && rawToken !== 'null' && rawToken !== 'undefined'
        ? String(rawToken).trim()
        : null;
    if (!liveToken) {
      setError({
        endpoint: '/api/admin/dashboard/books',
        status: 401,
        message: isArabic
          ? 'انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.'
          : 'Your session has expired. Please sign in again.',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const apiBase = getApiBase();
      const path = `/api/admin/dashboard/books?${params.toString()}`;
      const resp = await fetch(`${apiBase}${path}`, {
        headers: { Authorization: `Bearer ${liveToken}` },
        credentials: 'include',
      });
      if (!resp.ok) {
        let bodyMsg = '';
        try {
          const j = await resp.json();
          bodyMsg = j?.detail || JSON.stringify(j).slice(0, 200);
        } catch (_e) {
          bodyMsg = (await resp.text()).slice(0, 200);
        }
        setError({
          endpoint: '/api/admin/dashboard/books',
          status: resp.status,
          message: bodyMsg || resp.statusText || 'No detail',
        });
        return;
      }
      const json = await resp.json();
      setData(json);
    } catch (e) {
      setError({
        endpoint: '/api/admin/dashboard/books',
        status: 0,
        message: (e && e.message) ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [token, status, q, skip, isArabic]);

  useEffect(() => { load(); }, [load]);

  const pages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)), [data.total]);
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div data-testid="admin-books">
      <div className="admin-ads-header">
        <div>
          <h1 className="admin-ads-title" data-testid="admin-books-title">
            <BookOpen size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
            {isArabic ? 'إدارة الكتب' : 'Books Management'}
          </h1>
          <p className="admin-ads-subtitle">
            {isArabic
              ? 'كل قوائم الكتب التي ينشرها الطلاب على المنصة.'
              : 'All student-posted book listings, platform-wide.'}
          </p>
        </div>
        <button type="button" className="admin-btn" onClick={load} data-testid="admin-books-refresh">
          <RefreshCw size={14} />
          {isArabic ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      <div className="admin-ads-filters">
        <div className="admin-ads-search">
          <Search size={14} />
          <input
            type="text"
            value={qInput}
            placeholder={isArabic ? 'بحث في العنوان، المؤلف، أو المعرّف…' : 'Search title, author, or book_id…'}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSkip(0); setQ(qInput); } }}
            data-testid="admin-books-search-input"
          />
          {qInput && (
            <button type="button" className="admin-btn ghost" onClick={() => { setQInput(''); setQ(''); setSkip(0); }} data-testid="admin-books-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="admin-ads-status-filters" role="tablist">
          <button
            type="button"
            className={`admin-pill ${status === '' ? 'blue' : 'gray'}`}
            onClick={() => { setStatus(''); setSkip(0); }}
            data-testid="admin-books-filter-all"
            data-active={status === '' ? 'true' : 'false'}
            aria-pressed={status === ''}
            role="tab"
          >
            {isArabic ? 'الكل' : 'All'}
          </button>
          {ALL_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`admin-pill ${status === s ? STATUS_PILL[s] : 'gray'}`}
              onClick={() => { setStatus(s); setSkip(0); }}
              data-testid={`admin-books-filter-${s}`}
              data-active={status === s ? 'true' : 'false'}
              aria-pressed={status === s}
              role="tab"
            >
              {labels[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        {error && (
          <div
            className="admin-card"
            data-testid="admin-books-error"
            style={{
              margin: 12,
              borderInlineStart: '3px solid #ef4444',
              background: 'rgba(239, 68, 68, 0.06)',
            }}
          >
            <h4 style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <AlertOctagon size={14} />
              {isArabic ? 'تعذر تحميل الكتب' : 'Failed to load books'}
            </h4>
            <ul style={{ fontSize: 12, lineHeight: 1.7, marginTop: 8, paddingInlineStart: 18 }}>
              <li>{isArabic ? 'النقطة' : 'Endpoint'}: <code data-testid="admin-books-error-endpoint">{error.endpoint}</code></li>
              <li>{isArabic ? 'حالة HTTP' : 'HTTP status'}: <strong data-testid="admin-books-error-status">{error.status}</strong></li>
              <li>{isArabic ? 'التفاصيل' : 'Detail'}: <span data-testid="admin-books-error-detail">{error.message}</span></li>
            </ul>
            <button type="button" className="admin-btn" onClick={load} style={{ marginTop: 6 }} data-testid="admin-books-error-retry">
              <RefreshCw size={14} /> {isArabic ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        )}

        <div className="admin-ads-table-wrap">
          <table className="admin-ads-table" data-testid="admin-books-table">
            <thead>
              <tr>
                <th>{isArabic ? 'الكتاب' : 'Book'}</th>
                <th>{isArabic ? 'المالك' : 'Owner'}</th>
                <th>{isArabic ? 'الحالة' : 'Status'}</th>
                <th>{isArabic ? 'السعر' : 'Price'}</th>
                <th>{isArabic ? 'تم إنشاؤه' : 'Created'}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center' }}>
                  <Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}
                </td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--admin-text-dim)' }} data-testid="admin-books-empty">
                  <Inbox size={28} style={{ marginBottom: 8, opacity: 0.6 }} />
                  <div>{isArabic ? 'لا توجد كتب تطابق المرشحات.' : 'No books match the filters.'}</div>
                </td></tr>
              )}
              {!loading && data.items.map((b) => (
                <tr key={b.book_id} data-testid={`admin-books-row-${b.book_id}`}>
                  <td>
                    <div className="admin-ads-ad-cell">
                      {b.thumbnail
                        ? <img src={b.thumbnail} alt="" className="admin-ads-thumb" loading="lazy" />
                        : <div className="admin-ads-thumb placeholder" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BookOpen size={14} style={{ opacity: 0.5 }} />
                          </div>}
                      <div className="admin-ads-ad-meta">
                        <div className="admin-ads-ad-title">{b.title || '—'}</div>
                        <div className="admin-ads-ad-id">{b.author ? `${b.author} · ` : ''}{b.book_id}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-ads-owner">
                      <div>{b.owner?.name || '—'}</div>
                      <div className="admin-ads-owner-email">{b.owner?.email || ''}</div>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-pill ${STATUS_PILL[b.status] || 'gray'}`} data-testid={`admin-books-status-${b.book_id}`}>
                      {labels[b.status] || b.status}
                    </span>
                  </td>
                  <td className="admin-ads-date">
                    {b.price != null ? `${b.price} ${b.currency || ''}` : '—'}
                  </td>
                  <td className="admin-ads-date">
                    {b.created_at ? new Date(b.created_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-ads-pager">
          <span data-testid="admin-books-total">
            {isArabic ? `الإجمالي: ${data.total}` : `Total: ${data.total}`}
          </span>
          <div className="admin-ads-pager-controls">
            <button type="button" className="admin-btn" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="admin-books-page-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="admin-ads-page-label" data-testid="admin-books-page-label">
              {currentPage} / {pages}
            </span>
            <button type="button" className="admin-btn" disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="admin-books-page-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
